import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnumLabel, useEnums } from '../../../api/enums';
import { currentLanguage } from '../../../i18n';
import { Autocomplete, Input, Radios, type ErrorSummaryItem } from '../../../components/idsk';
import { searchReferences, type ReferenceOption } from '../../../form-engine/react/references';
import { useWizard } from './WizardContext';
import { WizardStep } from './WizardStep';
import { validateStep1, type StepErrors } from './validation';

/**
 * Step 1 "Názov, subjekt a typ inšpekcie": the name, the inspected subject (autocomplete over the subjects by name,
 * EDUID or municipality; editing the text clears the choice until a suggestion is chosen), the inspection type and –
 * revealed under a type that has tasks – the inspection task of that type (GOV.UK conditional reveal; the tasks of
 * other types are not in the DOM).
 */
export function Step1NameSubjectType() {
  const { t } = useTranslation();
  const lang = currentLanguage();
  const label = useEnumLabel();
  const enums = useEnums().data;
  const { state, update } = useWizard();
  const [errors, setErrors] = useState<StepErrors>({});
  const [submitCount, setSubmitCount] = useState(0);
  const tasksOf = (type: string) => (enums?.inspectionTasks ?? []).filter((task) => task.parent === type);

  const onContinue = () => {
    const e = validateStep1(state);
    setErrors(e);
    setSubmitCount((c) => c + 1);
    return Object.keys(e).length === 0;
  };
  const summary: ErrorSummaryItem[] = [
    ...(errors.name ? [{ targetId: 'inspection-name', text: t(errors.name) }] : []),
    ...(errors.subject ? [{ targetId: 'inspection-subject', text: t(errors.subject) }] : []),
    ...(errors.type ? [{ targetId: 'inspection-type', text: t(errors.type) }] : []),
    ...(errors.task ? [{ targetId: `inspection-task-${state.type}`, text: t(errors.task) }] : []),
  ];

  const chooseType = (type: string) => {
    const tasks = tasksOf(type);
    update({ type, taskRequired: tasks.length > 0, task: tasks.some((x) => x.code === state.task) ? state.task : '' });
  };

  return (
    <WizardStep step={1} title={t('wizard.step1.title')} errors={summary} submitCount={submitCount} onContinue={onContinue}>
      <Input
        id="inspection-name"
        name="name"
        label={t('wizard.step1.name')}
        required
        labelClassName="govuk-label--m"
        hint={t('wizard.step1.nameHint')}
        error={errors.name ? t(errors.name) : undefined}
        value={state.name}
        onChange={(e) => update({ name: e.target.value })}
      />
      <Autocomplete<ReferenceOption>
        id="inspection-subject"
        label={t('wizard.step1.subject')}
        required
        labelClassName="govuk-label--m"
        hint={t('wizard.step1.subjectHint')}
        error={errors.subject ? t(errors.subject) : undefined}
        search={(q, signal) => searchReferences('SUBJECT', q, signal)}
        getOptionKey={(o) => o.id}
        getOptionLabel={(o) => o.label}
        getOptionHint={(o) => o.hint}
        initialText={state.subject?.label ?? ''}
        onSelect={(o) => update({ subject: { id: o.id, label: o.label } })}
        onTextChange={() => {
          if (state.subject) update({ subject: null });
        }}
      />
      <Radios
        name="type"
        idPrefix="inspection-type"
        legend={t('wizard.step1.type')}
        required
        legendClassName="govuk-fieldset__legend--m"
        hint={t('wizard.step1.typeHint')}
        error={errors.type ? t(errors.type) : undefined}
        items={(enums?.inspectionTypes ?? []).map((type) => {
          const tasks = tasksOf(type.code);
          return {
            value: type.code,
            label: type.label[lang],
            hint: tasks.length === 0 ? t('wizard.step1.noTasks') : undefined,
            conditional:
              tasks.length > 0 ? (
                <Radios
                  name={`task-${type.code}`}
                  idPrefix={`inspection-task-${type.code}`}
                  legend={t('wizard.step1.task')}
                  required
                  legendClassName="govuk-fieldset__legend--s"
                  error={errors.task && state.type === type.code ? t(errors.task) : undefined}
                  items={tasks.map((task) => ({ value: task.code, label: label('inspectionTasks', task.code) }))}
                  value={state.task || null}
                  onChange={(task) => update({ task })}
                />
              ) : undefined,
          };
        })}
        value={state.type || null}
        onChange={chooseType}
      />
    </WizardStep>
  );
}
